import { A } from '@solidjs/router';
import { createMemo, createSignal, onCleanup, onMount, Show } from 'solid-js';
import { locale, t } from '@/shared/i18n';
import { trackMarketingEvent } from '@/shared/lib/marketing-analytics';
import FeatureMarketingLayout from './components/FeatureMarketingLayout';
import FeatureFaqSection from './components/FeatureFaqSection';

export default function BarcodeMobilePage() {
  onMount(() => {
    const schema = {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'SoftwareApplication',
          name: 'khoai.io — Barcode & Mobile',
          applicationCategory: 'BusinessApplication',
          operatingSystem: 'Web, iOS, Android',
          url: 'https://khoai.io/features/barcode-mobile',
          description:
            'Generate barcodes in seconds, scan from any phone, and move inventory across warehouses and stores with full mobile-first visibility and offline support.',
          offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
          publisher: {
            '@type': 'Organization',
            name: 'khoai.io',
            url: 'https://khoai.io',
          },
        },
        {
          '@type': 'BreadcrumbList',
          itemListElement: [
            {
              '@type': 'ListItem',
              position: 1,
              name: 'Home',
              item: 'https://khoai.io',
            },
            {
              '@type': 'ListItem',
              position: 2,
              name: 'Barcode & Mobile',
              item: 'https://khoai.io/features/barcode-mobile',
            },
          ],
        },
      ],
    };
    let script = document.getElementById(
      'ld-json-feature-barcode-mobile'
    ) as HTMLScriptElement | null;
    if (!script) {
      script = document.createElement('script');
      script.type = 'application/ld+json';
      script.id = 'ld-json-feature-barcode-mobile';
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify(schema);
    onCleanup(() =>
      document.getElementById('ld-json-feature-barcode-mobile')?.remove()
    );
  });

  const [barcodeImageFailed, setBarcodeImageFailed] = createSignal(false);
  const [sellingImage1Failed, setSellingImage1Failed] = createSignal(false);
  const [sellingImage2Failed, setSellingImage2Failed] = createSignal(false);
  const [historyImageFailed, setHistoryImageFailed] = createSignal(false);

  const localizedBarcodeImage = createMemo(() =>
    locale() === 'vi' ? '/barcode_printing_vn.jpg' : '/barcode_printing_en.jpg'
  );
  const localizedSellingImage1 = createMemo(() =>
    locale() === 'vi' ? '/mobile_selling_1_vn.jpg' : '/mobile_selling_1_en.jpg'
  );
  const localizedSellingImage2 = createMemo(() =>
    locale() === 'vi' ? '/mobile_selling_2_vn.jpg' : '/mobile_selling_2_en.jpg'
  );
  const localizedHistoryImage = createMemo(() =>
    locale() === 'vi'
      ? '/mobile_order_history_vn.jpg'
      : '/mobile_order_history_en.jpg'
  );

  return (
    <FeatureMarketingLayout
      pageTag={t('feature.barcode.tag')}
      title={t('feature.barcode.title')}
      subtitle={t('feature.barcode.subtitle')}
      lightTheme
      heroRevealOnScroll
    >
      {/* -- Section 1: Fast barcode printing -- */}
      <section class="bg-amber-50/55 py-16">
        <div class="mx-auto grid max-w-7xl gap-8 px-4 sm:px-6 lg:grid-cols-[1.04fr_0.96fr] lg:items-center lg:px-8">
          <div class="p-2 sm:p-4 lg:pr-2">
            <h2 class="text-3xl font-black text-slate-900">
              {t('feature.barcode.f1')}
            </h2>
            <p class="mt-4 text-base leading-relaxed text-slate-700">
              {t('feature.barcode.f1Desc')}
            </p>
            <ul class="mt-6 list-inside list-disc space-y-3 text-sm text-slate-700">
              <li>{t('feature.barcode.f1Bullet1')}</li>
              <li>{t('feature.barcode.f1Bullet2')}</li>
              <li>{t('feature.barcode.f1Bullet3')}</li>
            </ul>
          </div>
          <div class="p-0 sm:p-2 lg:flex lg:justify-end lg:pl-2">
            <Show
              when={!barcodeImageFailed()}
              fallback={
                <div class="flex min-h-[340px] w-full items-center justify-center rounded-2xl bg-slate-50/70 p-6 text-center text-sm text-slate-500">
                  {t('feature.barcode.visual1Caption')}
                </div>
              }
            >
              <img
                src={localizedBarcodeImage()}
                alt={t('feature.barcode.visual1Title')}
                class="block h-auto w-full max-w-[520px] rounded-2xl object-contain shadow-[0_18px_48px_rgba(15,23,42,0.12)]"
                loading="lazy"
                onError={() => setBarcodeImageFailed(true)}
              />
            </Show>
          </div>
        </div>
      </section>

      {/* -- Section 2: Mobile selling workflow -- */}
      <section class="bg-white py-16">
        <div class="mx-auto grid max-w-7xl gap-8 px-4 sm:px-6 lg:grid-cols-[1.28fr_0.72fr] lg:items-center lg:px-8">
          <div class="order-2 p-0 sm:p-2 lg:order-1 lg:pr-2">
            <div class="grid grid-cols-2 gap-3 lg:gap-24">
              <Show
                when={!sellingImage1Failed()}
                fallback={
                  <div class="flex min-h-[340px] w-full items-center justify-center rounded-2xl bg-slate-50/70 p-6 text-center text-sm text-slate-500">
                    {t('feature.barcode.visual2Caption')}
                  </div>
                }
              >
                <img
                  src={localizedSellingImage1()}
                  alt={`${t('feature.barcode.visual2Title')} 1`}
                  class="ml-auto block h-auto w-full max-w-[260px] rounded-2xl object-contain shadow-[0_18px_48px_rgba(15,23,42,0.12)]"
                  loading="lazy"
                  onError={() => setSellingImage1Failed(true)}
                />
              </Show>
              <Show
                when={!sellingImage2Failed()}
                fallback={
                  <div class="flex min-h-[340px] w-full items-center justify-center rounded-2xl bg-slate-50/70 p-6 text-center text-sm text-slate-500">
                    {t('feature.barcode.visual2Caption')}
                  </div>
                }
              >
                <img
                  src={localizedSellingImage2()}
                  alt={`${t('feature.barcode.visual2Title')} 2`}
                  class="mr-auto block h-auto w-full max-w-[260px] rounded-2xl object-contain shadow-[0_18px_48px_rgba(15,23,42,0.12)]"
                  loading="lazy"
                  onError={() => setSellingImage2Failed(true)}
                />
              </Show>
            </div>
          </div>
          <div class="order-1 p-2 sm:p-4 lg:order-2 lg:pl-2">
            <h2 class="text-3xl font-black text-slate-900">
              {t('feature.barcode.f2')}
            </h2>
            <p class="mt-4 text-base leading-relaxed text-slate-700">
              {t('feature.barcode.f2Desc')}
            </p>
            <ul class="mt-6 list-inside list-disc space-y-3 text-sm text-slate-700">
              <li>{t('feature.barcode.f2Bullet1')}</li>
              <li>{t('feature.barcode.f2Bullet2')}</li>
              <li>{t('feature.barcode.f2Bullet3')}</li>
            </ul>
          </div>
        </div>
      </section>

      {/* -- Section 3: Audited mobile action history -- */}
      <section class="bg-amber-50/55 py-16">
        <div class="mx-auto grid max-w-7xl gap-8 px-4 sm:px-6 lg:grid-cols-[0.72fr_1.28fr] lg:items-center lg:px-8">
          <div class="p-2 sm:p-4 lg:pr-2">
            <h2 class="text-3xl font-black text-slate-900">
              {t('feature.barcode.f3')}
            </h2>
            <p class="mt-4 text-base leading-relaxed text-slate-700">
              {t('feature.barcode.f3Desc')}
            </p>
            <ul class="mt-6 list-inside list-disc space-y-3 text-sm text-slate-700">
              <li>{t('feature.barcode.f3Bullet1')}</li>
              <li>{t('feature.barcode.f3Bullet2')}</li>
              <li>{t('feature.barcode.f3Bullet3')}</li>
            </ul>
          </div>
          <div class="p-0 sm:p-2 lg:pl-2">
            <Show
              when={!historyImageFailed()}
              fallback={
                <div class="flex min-h-[340px] w-full items-center justify-center rounded-2xl bg-slate-50/70 p-6 text-center text-sm text-slate-500">
                  {t('feature.barcode.visual3Caption')}
                </div>
              }
            >
              <img
                src={localizedHistoryImage()}
                alt={t('feature.barcode.visual3Title')}
                class="block h-auto w-full rounded-2xl object-contain shadow-[0_18px_48px_rgba(15,23,42,0.12)]"
                loading="lazy"
                onError={() => setHistoryImageFailed(true)}
              />
            </Show>
          </div>
        </div>
      </section>

      {/* -- CTA -- */}
      <section class="bg-white py-16">
        <div class="mx-auto max-w-7xl px-4 text-center sm:px-6 lg:px-8">
          <h2 class="text-4xl font-black text-slate-900">
            {t('feature.barcode.cta')}
          </h2>
          <p class="mx-auto mt-4 max-w-2xl text-base text-slate-700">
            {t('feature.barcode.ctaSubtitle')}
          </p>
          <A
            href="/signup"
            onClick={() =>
              trackMarketingEvent('feature_cta_click', {
                feature: 'barcode-mobile',
                target: '/signup',
              })
            }
            class="mt-8 inline-block rounded-xl bg-amber-600 px-8 py-3.5 text-lg font-bold text-white hover:bg-amber-700"
          >
            {t('feature.cta')}
          </A>
        </div>
      </section>

      <FeatureFaqSection
        titleKey="feature.barcode.faq.title"
        scriptId="ld-json-barcode-faq"
        items={[
          { q: t('feature.barcode.faq.q1'), a: t('feature.barcode.faq.a1') },
          { q: t('feature.barcode.faq.q2'), a: t('feature.barcode.faq.a2') },
          { q: t('feature.barcode.faq.q3'), a: t('feature.barcode.faq.a3') },
          { q: t('feature.barcode.faq.q4'), a: t('feature.barcode.faq.a4') },
          { q: t('feature.barcode.faq.q5'), a: t('feature.barcode.faq.a5') },
        ]}
      />
    </FeatureMarketingLayout>
  );
}
